import React from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const SalesRevenue = ({ salesAnalytics = [], text }) => {
    const isDataAvailable = salesAnalytics.length > 0;

    const generateYearlyLabels = () => [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const getSalesDataForYear = () => {
        const labels = generateYearlyLabels();
        const salesData = new Array(12).fill(0);

        salesAnalytics.forEach(sale => {
            const saleMonth = new Date(sale.month).getMonth();
            salesData[saleMonth] = parseFloat(sale.total_sales);
        });

        return salesData;
    };

    // Calculate total sales and percentage increase
    const totalSales = isDataAvailable ?
        salesAnalytics.reduce((sum, sale) => sum + parseFloat(sale.total_sales), 0) : 0;

    const currentMonthSales = isDataAvailable ?
        getSalesDataForYear()[new Date().getMonth()] : 0;

    const previousMonthSales = isDataAvailable ?
        getSalesDataForYear()[new Date().getMonth() - 1 < 0 ? 11 : new Date().getMonth() - 1] : 0;

    const percentageChange = previousMonthSales > 0 ?
        ((currentMonthSales - previousMonthSales) / previousMonthSales * 100).toFixed(1) : 0;

    const data = {
        labels: generateYearlyLabels(),
        datasets: [
            {
                label: 'Sales',
                data: getSalesDataForYear(),
                fill: true,
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                    gradient.addColorStop(0, 'rgba(248, 168, 27, 0.25)');
                    gradient.addColorStop(1, 'rgba(248, 168, 27, 0.02)');
                    return gradient;
                },
                borderColor: '#F8A81B',
                pointBackgroundColor: '#FFFFFF',
                pointBorderColor: '#F8A81B',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                tension: 0.4,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                    color: '#9CA3AF',
                },
                border: {
                    display: false,
                }
            },
            y: {
                grid: {
                    borderDash: [3, 3],
                    color: 'rgba(156, 163, 175, 0.1)',
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                    color: '#9CA3AF',
                    padding: 10,
                    callback: function (value) {
                        return '$' + value.toLocaleString();
                    }
                },
                border: {
                    display: false,
                }
            },
        },
        elements: {
            line: {
                borderWidth: 3,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#111827',
                bodyColor: '#4B5563',
                bodyFont: {
                    size: 12,
                },
                padding: 12,
                borderColor: 'rgba(229, 231, 235, 1)',
                borderWidth: 1,
                displayColors: false,
                callbacks: {
                    label: function (context) {
                        return '$ ' + context.parsed.y.toLocaleString();
                    }
                }
            }
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        hover: {
            mode: 'index',
            intersect: false,
        },
    };

    return (
        <div className="w-full h-[450px] bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-gray-900 font-semibold text-lg mb-1">{text}</h3>
                        {isDataAvailable && (
                            <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold text-gray-900">
                                    ${totalSales.toLocaleString()}
                                </span>
                                {percentageChange > 0 && (
                                    <div className="flex items-center text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                        <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        {percentageChange}%
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2">
                        <button className="text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg transition-colors">
                            Monthly
                        </button>
                        <button className="text-xs font-medium text-gray-500 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors">
                            Yearly
                        </button>
                    </div>
                </div>
            </div>

            <div className="px-6 h-[320px]">
                {!isDataAvailable ? (
                    <div className="flex justify-center items-center h-full">
                        <p className="text-gray-500">No data available</p>
                    </div>
                ) : (
                    <Line data={data} options={options} />
                )}
            </div>
        </div>
    );
};

export default SalesRevenue;